

import { useEffect, useState, useCallback } from 'react'
import { Field, Label, Control, InlineWrapper, StepFormWrapper } from '../../styles/login.style'
import { DropzoneContainer } from '../../styles/upload-dialog.style'
import Dropzone from 'react-dropzone'
import { X } from 'react-bootstrap-icons'
import { registrationData, ProfilePatch } from '../../hooks/auth.service'
import UniversityDropdown from '../../components/register/UniversityDropdown'
import { cambridgeColleges, classificationOptions, oxfordColleges } from '../../types/schemas/abc'


interface Props {
  data: ProfilePatch 
  onChange: (d: ProfilePatch) => void
  onValidityChange: (ok: boolean) => void
}

export default function CollegeDetailsStep({
  data,
  onChange,
  onValidityChange,
}: Props) {
  const {
    university = 'Oxford',
    college = 'Select',
    degreeCourse = '',
    gradYear,
    proofFile,
    classification = 'Select',
  } = data

  const [fileName, setFileName] = useState(proofFile?.name || '')

  const onDrop = useCallback(
    (files: File[]) => {
      if (!files[0]) return
      setFileName(files[0].name)
      onChange({ ...data, proofFile: files[0] })
    },
    [data, onChange]
  )

  const removeFile = () => {
    setFileName('')
    onChange({ ...data, proofFile: undefined })
  }

  const showClassification = !!gradYear && gradYear < new Date().getFullYear();

  useEffect(() => {
    if (!showClassification && data.classification) {
      const { classification, ...rest } = data;
      onChange(rest);
    }
  }, [showClassification, data, onChange]);

  const isOK =
    degreeCourse.trim().length > 2 &&
    typeof gradYear === 'number' &&
    Number.isFinite(gradYear) &&
    gradYear >= 1900 &&
    gradYear <= new Date().getFullYear() + 6 &&
    !!proofFile

  useEffect(() => onValidityChange(isOK), [isOK, onValidityChange])

  const uniOptions: string[] = ['Oxford', 'Cambridge']

  const collegeOptions =
    university === 'Oxford' ? oxfordColleges : cambridgeColleges



  return (
    // <>
    <StepFormWrapper>
      {/* University */}
      <Field name="uni">

        <InlineWrapper>
        <Label css={{ textAlign: 'right' }}>University</Label>
        <UniversityDropdown
          value={university}
          options={uniOptions}
          onChange={(val) =>
            onChange({ ...data, university: val as 'Oxford' | 'Cambridge' })
          }
        />
        </InlineWrapper>

      </Field>

      <Field name="college">
        <InlineWrapper>
        <Label css={{ textAlign: 'right', marginRight: '0.85rem'}}>College</Label>
        <UniversityDropdown
          value={college}
          options={collegeOptions}
          onChange={(val) =>
            onChange({ ...data, college: val}) 
          }
        />
        </InlineWrapper>
      </Field>

      <Field name="course" css={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <Label>Course</Label>
        <div style={{ position: 'relative', width: '100%' }}>
        <Control
          value={degreeCourse}
          placeholder='e.g History BA'
          onChange={(e) =>
            onChange({ ...data, degreeCourse: e.currentTarget.value })
          }
          required
        />
        </div>
      </Field>

      <Field name="year" css={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <Label>Graduation year</Label>
        <div style={{ position: 'relative', width: '100%' }}>
        <Control
          type="number"
          placeholder="e.g. 2027"
          value={typeof gradYear === 'number' && Number.isFinite(gradYear) ? gradYear : ''}
          onChange={(e) => {
            const v = e.currentTarget.value
            onChange({ ...data, gradYear: v === '' ? undefined : Number(v) })
          }}
          required
        />
        </div>
      </Field>

      {showClassification && (
        <Field name="classification">
          <InlineWrapper>
            <Label css={{ textAlign: 'right', marginRight: '0.85rem' }}>Classification</Label>
            <UniversityDropdown
              value={classification}
              options={['Select', ...classificationOptions]}
              onChange={(val) =>
                onChange({ ...data, classification: val })
              }
            />
          </InlineWrapper>
        </Field>
      )}

      {/* Proof upload (Dropzone) */}
      <Field name="proof" css={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        {/* <InlineWrapper> */}
        <Label>Upload proof (Bod Card / degree cert)</Label>
        <div style={{ position: 'relative', width: '100%' }}>
          <Dropzone
            onDrop={onDrop}
            accept={{ 'application/pdf': [], 'image/*': [] }}
            maxFiles={1}
          >
            {({ getRootProps, getInputProps, isDragActive }) => (
              <DropzoneContainer
                {...getRootProps()}
                state={proofFile ? 'success' : 'idle'}
                css={{
                  cursor: 'pointer',
                  background: isDragActive
                    ? '$elementBackgroundHover'
                    : '$elementBackground',
                  mb: '0.5rem',
                }}
              >
                <input {...getInputProps()} />
                {fileName ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <span>{fileName}</span>
                    <X
                      size={18}
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        removeFile()
                      }}
                    />
                  </div>
                ) : (
                  <p style={{ margin: 0 }}>
                    Drag PDF / image here or click to select
                  </p>
                )}
              </DropzoneContainer>
            )}
          </Dropzone>
          {fileName && (
            <small style={{ fontSize: '0.8rem' }}>
              Selected: {fileName}
            </small>
          )}
        {/* </InlineWrapper> */}
        </div>
      </Field>
      </StepFormWrapper>

  )
}
